/**
 * Admin-presence client: pings the server while the dashboard is open so
 * the server knows to persist incoming agent screenshots.
 *
 * Heartbeat protocol:
 *   - start()  → registers an interval (10s) hitting POST /admin/presence/heartbeat
 *   - stop()   → cancels the interval and POST /admin/presence/offline once
 *   - start()  is idempotent; second call is a no-op
 *
 * Auto-stop on tab visibility change (hidden) so a backgrounded tab
 * doesn't keep the server "online" forever — the server's 30s timeout
 * would catch it eventually, but stopping proactively is cleaner and
 * avoids an avalanche of backgrounded tabs all racking up flag flips.
 *
 * The `onPresenceChange` callback is invoked when the server tells us
 * (via Socket.IO admin:presence event) that the flag has flipped.
 */
import { httpClient } from '@/lib/axios'
import { getSocket } from '@/lib/socket'
import { SOCKET_EVENTS } from '@monitor/shared'

export type PresenceChangeListener = (online: boolean, persistenceMode?: 'live' | 'transient') => void

const HEARTBEAT_INTERVAL_MS = 10_000

let intervalId: number | null = null
let listeners: PresenceChangeListener[] = []
let lastOnline: boolean = false
let lastMode: 'live' | 'transient' = 'transient'
let installedSocketListener = false

/**
 * Toggle persistence on the server. Returns the new mode so the caller
 * can update local UI state without waiting for the socket push.
 */
export async function setPersistenceModeApi(mode: 'live' | 'transient'): Promise<{ persistenceMode: 'live' | 'transient' }> {
  const r = await httpClient.post<{ persistenceMode: 'live' | 'transient' }>('/api/admin/presence/mode', { mode })
  lastMode = r.data.persistenceMode
  for (const fn of listeners) {
    try {
      fn(lastOnline, lastMode)
    } catch {
      /* swallow */
    }
  }
  return r.data
}

export function getPersistenceMode(): 'live' | 'transient' {
  return lastMode
}

export async function startPresence(): Promise<void> {
  // First heartbeat kicks the flag on immediately so the very next
  // screenshot from any agent is persisted, not buffered.
  try {
    const r = await httpClient.post<{ online: boolean }>('/api/admin/presence/heartbeat', {})
    lastOnline = r.data.online
  } catch {
    // 401 → user logged out; heartbeat will be re-armed by the next login
  }
  if (intervalId !== null) return
  intervalId = window.setInterval(async () => {
    if (document.visibilityState !== 'visible') return
    try {
      const r = await httpClient.post<{ online: boolean }>('/api/admin/presence/heartbeat', {})
      lastOnline = r.data.online
    } catch {
      /* swallow — next tick will retry */
    }
  }, HEARTBEAT_INTERVAL_MS)

  // Hook visibility / lifecycle events exactly once.
  if (!installedSocketListener) {
    installedSocketListener = true
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // Tab went background. Tell the server right away so we don't
        // keep the "save" flag on for 30s after the user closed the lid.
        void stopPresence({ silent: true })
      } else if (document.visibilityState === 'visible' && intervalId !== null) {
        // Re-arm the heartbeat immediately on focus.
        void startPresence()
      }
    })
    window.addEventListener('beforeunload', () => {
      // Use sendBeacon for a fire-and-forget offline ping so the server
      // gets the message even as the page is closing.
      try {
        const url = '/api/admin/presence/offline'
        const blob = new Blob([''], { type: 'application/json' })
        navigator.sendBeacon?.(url, blob)
      } catch {
        /* swallow */
      }
    })

    // Listen to server push so the dashboard can show a "Now saving" toast.
    const socket = getSocket()
    socket.on(SOCKET_EVENTS.ADMIN_PRESENCE, (msg: { online: boolean; persistenceMode?: 'live' | 'transient' }) => {
      lastOnline = msg.online
      if (msg.persistenceMode) lastMode = msg.persistenceMode
      for (const fn of listeners) {
        try {
          fn(msg.online, lastMode)
        } catch {
          /* swallow */
        }
      }
    })
  }
}

export async function stopPresence(opts: { silent?: boolean } = {}): Promise<void> {
  if (intervalId !== null) {
    window.clearInterval(intervalId)
    intervalId = null
  }
  if (opts.silent) return
  try {
    await httpClient.post('/api/admin/presence/offline', {})
    lastOnline = false
  } catch {
    /* swallow */
  }
}

export function getPresenceOnline(): boolean {
  return lastOnline
}

export function onPresenceChange(fn: PresenceChangeListener): () => void {
  listeners.push(fn)
  return () => {
    listeners = listeners.filter((f) => f !== fn)
  }
}
